import "@livekit/components-styles";
import { useEffect } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useCookies } from "react-cookie";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Routes from "./Routes";
import Footer from "./components/Footer";
import Header from "./components/Header";
import ActionBubble from "./components/actionBubble/ActionBubble";
import { localePath, getLocaleFromPath } from "./i18n/config";
import { metadataTranslations } from "./i18n/metadata";
import useLogin from "./hooks/useLogin";
import useWebView from "./hooks/useWebView";
import { ResetShoppingcart } from "./redux/actions";
import { AppState } from "./redux/types";
import { GOOGLE_TAG_MANAGER_ID, installGoogleTagManager, shouldLoadGoogleTagManager } from "./utils/googleTagManager";
import { isPurchaseFlowPath } from "./utils/purchaseFlow";

const App = () => {
    const [cookies, setCookie, removeCookie] = useCookies();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const cartItems = useSelector((state: AppState) => state.cart.inCart);
    const isAdminRoute = location.pathname.startsWith("/admin");
    useWebView();

    useEffect(() => {
        if (!isPurchaseFlowPath(location.pathname)) {
            sessionStorage.removeItem("confirmedBuyRegion");
            if (cartItems.length > 0) {
                dispatch(ResetShoppingcart());
            }
        }
    }, [cartItems.length, dispatch, location.pathname]);

    useEffect(() => {
        if (shouldLoadGoogleTagManager(location.pathname)) {
            installGoogleTagManager();
        }
    }, [location.pathname]);

    useEffect(() => {
        if (cookies.consent !== "all") {
            removeCookie("_ga");
            removeCookie("_gat");
            removeCookie("_gid");
        } else {
            const script_mp = document.createElement("script");
            script_mp.textContent = `!function(f,b,e,v,n,t,s)
                    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                        n.queue=[];t=b.createElement(e);t.async=!0;
                        t.src=v;s=b.getElementsByTagName(e)[0];
                        s.parentNode.insertBefore(t,s)}(window, document,'script',
                    'https://connect.facebook.net/en_US/fbevents.js');
                    fbq('init', '${process.env.REACT_APP_META_PIXEL_ID}');
                    fbq('track', 'PageView');`;
            document.head.appendChild(script_mp);
        }
    }, [cookies.consent, removeCookie]);

    useEffect(() => {
        const root = document.documentElement;
        if (isAdminRoute) {
            root.setAttribute("translate", "no");
            root.classList.add("notranslate");
            document.body.classList.add("notranslate");
            return;
        }
        root.removeAttribute("translate");
        root.classList.remove("notranslate");
        document.body.classList.remove("notranslate");
    }, [isAdminRoute]);

    const onChange = (name: any) => {
        setCookie("consent", name, { path: "/" });
    };
    useLogin();

    const locale = getLocaleFromPath(location.pathname);
    const meta = metadataTranslations.default[locale] || metadataTranslations.default.fi;

    let description = meta.description;
    if (window.location.host.split(".")[0] === "www") {
        description += " - WWW versio.";
    } else {
        description += ".";
    }

    const title = meta.title;

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
            </Helmet>
            {GOOGLE_TAG_MANAGER_ID && shouldLoadGoogleTagManager(location.pathname) && (
                <noscript>
                    <iframe
                        title="Google Tag Manager"
                        src={`https://www.googletagmanager.com/ns.html?id=${GOOGLE_TAG_MANAGER_ID}`}
                        height="0"
                        width="0"
                        style={{ display: "none", visibility: "hidden" }}
                    />
                </noscript>
            )}
            <Header className={window.location.pathname.split("/")[1] === "meet" ? "prejoin-room" : ""} />
            <Routes />
            {!isPurchaseFlowPath(location.pathname) && <ActionBubble onClick={() => history.push(localePath("yhteydenotto"))} showOnRoutes={["/*"]} />}
            <Footer className={window.location.pathname.split("/")[1] === "meet" ? "prejoin-room" : ""} />
            <ToastContainer />
            {cookies.consent === "all" && (
                <img
                    src="https://secure.adnxs.com/seg?member_id=7458&add_code=799995B&t=2"
                    width="1"
                    height="1"
                    style={{
                        display: "none",
                    }}
                    alt="sanoma"
                />
            )}
            {!["all", "required"].includes(cookies.consent) && (
                <Container className={`consent_banner ${window.location.pathname.split("/")[1] === "meet" ? "prejoin-room" : ""}`}>
                    <Row
                        className="align-items-center justify-content-center"
                        style={{
                            minHeight: 64,
                            gap: 16,
                        }}
                    >
                        <Col xs="auto">
                            <div style={{ textAlign: "center" }}>Tämä sivusto käyttää evästeitä</div>
                        </Col>
                        <Row className="p-2  p-md-0">
                            <Col xs="auto">
                                <Button
                                    className="consent_button"
                                    style={{
                                        minWidth: 150,
                                        padding: "8px 16px",
                                    }}
                                    onClick={() => onChange("all")}
                                >
                                    Kaikki
                                </Button>
                            </Col>
                            <Col xs="auto">
                                <Button
                                    className="consent_button"
                                    style={{
                                        minWidth: 150,
                                        padding: "8px 16px",
                                    }}
                                    onClick={() => onChange("required")}
                                >
                                    Välttämättömät
                                </Button>
                            </Col>
                        </Row>
                    </Row>
                </Container>
            )}
        </>
    );
};

export default App;
